import { normalizeLexiconTrigramText } from './lexicon-trigram'
import type { Language } from './lexicon-model'

export type FrequencyRow = {
  word: string
  rank: number
  definitionCount: number
}

export type FrequencyListPort = (language: Language) => Promise<FrequencyRow[]>

export type LemmaStatus = 'toLearn' | 'inProgress' | 'learnt'

export type AdoptedLemmaRow = {
  pageIds?: string[]
  headword: string
  senseCount: number
  transcription: string
  translations: Record<string, string>
}

export type LemmaProgress = FrequencyRow & {
  senseCount: number
  status: LemmaStatus
  adopted?: AdoptedLemmaRow
}

export function parseFrequencyList(text: string): FrequencyRow[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => {
      const separator = line.lastIndexOf(',')
      const word = line.slice(0, separator)
      const definitionCount = Number(line.slice(separator + 1))
      if (separator < 1 || !Number.isFinite(definitionCount)) {
        throw new Error(`Invalid frequency row: ${line}`)
      }
      return { word, rank: index + 1, definitionCount }
    })
}

export function createStaticFrequencyList(
  fetch: typeof globalThis.fetch,
): FrequencyListPort {
  return async (language) => {
    const response = await fetch(frequencyListPath(language))
    if (response.status === 404) return []
    if (!response.ok) {
      throw new Error(`Frequency list request failed: ${response.status}`)
    }
    if (!response.headers.get('content-type')?.includes('text/plain')) {
      return []
    }
    return parseFrequencyList(await response.text())
  }
}

export function frequencyListPath(language: Language): string {
  return `/lexicon/${language}-frequency.txt`
}

export function vocabularyProgress(
  lemmas: AdoptedLemmaRow[],
  list: FrequencyRow[],
): LemmaProgress[] {
  const lemmasByHeadword = new Map<string, AdoptedLemmaRow>()
  const headwords = new Set<string>()
  for (const lemma of lemmas) {
    if (headwords.has(lemma.headword)) throw new Error('INV: one summary row per lemma (GROUP BY headword)')
    headwords.add(lemma.headword)
    const key = vocabularyLemma(lemma.headword)
    const previous = lemmasByHeadword.get(key)
    if (previous) {
      lemmasByHeadword.set(key, { ...previous, senseCount: previous.senseCount + lemma.senseCount })
    } else lemmasByHeadword.set(key, lemma)
  }

  return list.map((row) => {
    const adopted = lemmasByHeadword.get(vocabularyLemma(row.word))
    const senseCount = adopted?.senseCount ?? 0
    const status: LemmaStatus = adopted
      ? senseCount >= row.definitionCount
        ? 'learnt'
        : 'inProgress'
      : 'toLearn'
    return { ...row, senseCount, status, adopted }
  })
}

export function rankBadge(rank: number): string {
  if (rank <= 1_000) return `#${rank}`
  const lower = Math.floor((rank - 1) / 1_000)
  return `#${lower}k–${lower + 1}k`
}

export function partitionProgress(
  rows: LemmaProgress[],
): Record<LemmaStatus, LemmaProgress[]> {
  const result: Record<LemmaStatus, LemmaProgress[]> = {
    toLearn: [],
    inProgress: [],
    learnt: [],
  }
  for (const row of rows) result[row.status].push(row)
  return result
}

/** Frequency lists use the lemma; dictionary articles keep the noun's article. */
export function vocabularyLemma(headword: string): string {
  return normalizeLexiconTrigramText(headword).trim().replace(/^(der|die|das)\s+/u, '')
}
